// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useMemo, useCallback } from 'react';

import { type EvolveApiContextType, EvolveApiContext } from '@evolvemep/api-ts';

import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';

export const getBaseApiUrl = () => {
  // If running via Vite, pull the API URL from the Vite config
  const { DEV, VITE_APP_API_BASE_URL } = import.meta.env;
  if (DEV) {
    if (VITE_APP_API_BASE_URL) return VITE_APP_API_BASE_URL;

    const error = new Error('Missing VITE_APP_API_BASE_URL in .env');
    // oxlint-disable-next-line no-console
    console.error(error);
    throw error;
  }

  // Otherwise, infer the API URL from the current hostname
  const environment = window.location.hostname.replace(/^foresite\./, '').replace(/\.?evolvemep\.com$/, '');
  return `https://api.${environment}.evolvemep.com`.replaceAll('..', '.');
};

export const EvolveApiProvider = ({ children }: { children: ReactNode }) => {
  const { showError } = useMantineNotifications();
  const baseUrl = useMemo(() => getBaseApiUrl(), []);
  const getBaseApiUrlWrapped = useCallback<EvolveApiContextType['getBaseApiUrl']>(async () => baseUrl, [baseUrl]);
  return <EvolveApiContext value={{ showError, getBaseApiUrl: getBaseApiUrlWrapped }}>{children}</EvolveApiContext>;
};
